import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Bookmark as BookmarkIcon,
  Description as TextIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { jobsApi } from '../services/api';
import { Job, User, Bookmark } from '../types';




interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserJobs: React.FC = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [tabRendering, setTabRendering] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadSnackbarMessage, setDownloadSnackbarMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUserJobs = async () => {
    try {
      setLoading(true);
      const transcriptionJobs = await jobsApi.getUserJobs(
        page + 1,
        rowsPerPage,
        statusFilter || undefined,
        searchQuery || undefined
      );
      setJobs(transcriptionJobs.data);
      if (transcriptionJobs.total !== undefined) setTotal(transcriptionJobs.total);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserJobs();
  }, [statusFilter, searchQuery, page, rowsPerPage]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await jobsApi.uploadAudio(file);
      setUploadSuccessMessage(true);
      await fetchUserJobs();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon color="success" />;
      case 'processing':
        return <PlayIcon color="primary" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 100) / 100;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleViewJob = (job: Job) => {
    setSelectedJob(job);
    setDialogOpen(true);
    setTabValue(0);
    fetchBookmarks(job.id);
  };

  const fetchBookmarks = async (jobId: string) => {
    try {
      setBookmarksLoading(true);
      const bookmarksData = await jobsApi.getBookmarks(jobId);
      setBookmarks(bookmarksData);
    } catch (err) {
      console.error('Error fetching bookmarks:', err);
      setBookmarks([]);
    } finally {
      setBookmarksLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedJob(null);
    setBookmarks([]);
    setBookmarksLoading(false);
  };

  const handleDownloadTranscription = async () => {
    if (!selectedJob) return;
    setDownloadLoading(true);
    setDownloadSnackbarMessage(null);
    try {
      const blob = await jobsApi.downloadTranscription(selectedJob.id);
      const baseName = selectedJob.originalName || selectedJob.filename || `transcription-${selectedJob.id}`;
      const filename = baseName.endsWith('.txt') ? baseName : `${baseName}.txt`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: string } }).response?.status === 202
          ? 'Transcription not ready yet.'
          : (err as { response?: { data?: string } }).response?.data ?? 'Download failed.'
        : 'Download failed.';
      setDownloadSnackbarMessage(message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1) { // Bookmarks tab
      setTabRendering(true);
      // Use setTimeout to allow React to render the loading state
      setTimeout(() => setTabRendering(false), 0);
    }
    setTabValue(newValue);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        accept=".mp3,audio/mpeg"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
      <Typography variant="h4" gutterBottom>
        My Jobs
      </Typography>

       {/* Filters */}
       <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Upload recording'}
          </Button>
          <TextField
            label="Search jobs"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      <Card>
        <CardContent>
        {loading && <LinearProgress />}
          <List>
            {jobs.map((job) => (
              <ListItem key={job.id} divider>
                <ListItemIcon>
                  {getStatusIcon(job.status)}
                </ListItemIcon>
                <ListItemText
                  primary={job.filename}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Created: {new Date(job.uploadedAt).toLocaleDateString()}
                        {job.duration && ` • Duration: ${formatDuration((job.duration)/60)}`}
                        {job.bookmarkCount && ` • Bookmarks: ${job.bookmarkCount}`}
                      </Typography>
                    </Box>
                  }
                />
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={job.status} 
                    color={getStatusColor(job.status) as any}
                    size="small"
                  />
                  {job.status === 'completed' && (
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewJob(job)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
          
          {jobs.length === 0 && (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No jobs found. Start by uploading your first recording!
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedJob?.filename}
          <Typography variant="body2" color="textSecondary">
            Created: {selectedJob ? new Date(selectedJob.uploadedAt).toLocaleDateString() : ''}
            {selectedJob?.duration && ` • Duration: ${formatDuration((selectedJob.duration)/60)}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Transcription" icon={<TextIcon />} iconPosition="start" />
              <Tab label="Bookmarks" icon={<BookmarkIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <Typography variant="body1" component="pre" sx={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                {selectedJob?.transcription || 'No transcription available'}
              </Typography>
            </Paper>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {bookmarksLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : tabRendering ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Skeleton variant="rectangular" height={60} />
                  </Box>
                ))}
              </Box>
            ) : bookmarks.length > 0 ? (
              <Box>
                {bookmarks.map((bookmark) => (
                  <Accordion key={bookmark.id} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <Typography variant="body2" color="primary" sx={{ minWidth: 80 }}>
                          {formatTime(bookmark.time)}
                        </Typography>
                        <Typography variant="body1" sx={{ flexGrow: 1 }}>
                          {bookmark.caption}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="textSecondary">
                        Version: {bookmark.version}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                No bookmarks available for this job.
              </Typography>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button 
            startIcon={downloadLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            variant="contained"
            disabled={downloadLoading}
            onClick={handleDownloadTranscription}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={uploadSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setUploadSuccessMessage(false)}
        message="Recording uploaded; transcription started."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!downloadSnackbarMessage}
        autoHideDuration={6000}
        onClose={() => setDownloadSnackbarMessage(null)}
        message={downloadSnackbarMessage ?? ''}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default UserJobs; 